import { PatientInfoCard } from "@/components/PatientInfo/PatientInfoCard/PatientInfoCard";
import { handleOpenChatPopup, tw } from "@/utils";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { TransferInfo } from "./TransferInfo";
import {
  smallTableHeaderStyle,
  smallTableRowTextStyle,
} from "@/components/tableComponents/tableStyles";
import { HospitalResourceGrid } from "./HospitalResourceGrid/HospitalResourceGrid";
import ChatBubble from "./assets/chatBubble.svg?react";
import PageBack from "@/assets/pageBack.svg?react";
import { StatusProgressBar } from "../../../components/StatusProgressBar/StatusProgressBar";
import Modal from "@/components/Modal";
import { ICUModal } from "./ICUModal/ICUModal";
import FollowUpIcon from "@/assets/followUpIcon.svg?react";
import FilterIcon from "@/assets/filter.svg?react";
import { useGetOperationInfo } from "./api";
import { operationCodeToName } from "@/data/operations";

interface CareInsightDetailProps {
  // props 정의
}

export const CareInsightDetail = (props: CareInsightDetailProps) => {
  const {} = props;
  const { requestId } = useParams();
  const navigate = useNavigate();

  const [isICUModalOpen, setIsICUModalOpen] = useState<boolean>(false);

  if (!requestId) throw new Error();

  const { data, isSuccess } = useGetOperationInfo(requestId);

  return (
    <>
      <div className="w-full h-full">
        <header
          className={tw(
            "flex items-center justify-between w-full h-[76px] px-[30px] py-[20px] bg-white",
            "border-solid border-b-[1px] border-gray-g50"
          )}
        >
          <div
            className={tw(
              "flex items-center gap-[16px] cursor-pointer",
              "text-lg font-medium text-gray-g200"
            )}
            onClick={() => {
              navigate("/careInsight");
            }}
          >
            <PageBack />
            이송 요청 목록
          </div>
          <StatusProgressBar />
        </header>
        <div className="flex w-full h-[calc(100%-76px)] overflow-auto">
          <div className="w-[clamp(500px,40%,580px)] shrink-0 h-full p-[16px]">
            <div className="w-full h-full">
              <PatientInfoCard requestId={requestId} />
            </div>
          </div>
          <div className="grow flex min-w-0 h-full bg-white shadow-patient-box">
            <div className={tw("h-full", "w-full")}>
              <div className="w-full h-full">
                <div
                  className={tw(
                    "w-full h-[106px] p-[18px]",
                    "border-solid border-b-[1px] border-gray-g30"
                  )}
                >
                  <TransferInfo requestId={requestId} />
                </div>
                <div className="w-full h-[calc(100%-106px)]">
                  <div className="flex flex-col gap-[18px] w-full h-full p-[18px] overflow-y-auto overflow-x-hidden">
                    {isSuccess && (
                      <div className="w-full h-fit bg-white">
                        <div className="flex items-center h-[32px] gap-[10px]">
                          <div
                            className={tw(
                              "flex items-center justify-center",
                              "w-fit h-[32px] px-[12px] rounded-[6px] bg-purple-30",
                              "text-base font-semibold text-purple-100"
                            )}
                          >
                            경북대학교병원
                          </div>
                          <div className="text-base font-semibold text-gray-g300 leading-[22px]">
                            시술/수술 가능 여부
                          </div>
                          <button
                            className={tw(
                              "ml-auto w-fit h-[32px] bg-white px-[16px]",
                              "border-solid border-[1px] border-purple-100 rounded-[6px]",
                              "text-sm font-medium text-purple-100"
                            )}
                            onClick={() => {
                              setIsICUModalOpen((prev) => !prev);
                            }}
                          >
                            우리 병원 ICU 현황
                          </button>
                        </div>

                        <div className="w-full h-fit mt-[10px]">
                          {data.result.data.length !== 0 ? (
                            <div
                              className={tw(
                                "w-full h-fit",
                                "border-solid border-[1px] border-gray-g30 rounded-[6px]"
                              )}
                            >
                              <div className={tw(smallTableHeaderStyle)}>
                                <div className="flex-180">항목</div>
                                <div className="flex-220">진료과목</div>
                                <div className="flex-130">의료진</div>
                                <div className="flex-180">전화번호</div>
                                <div className="flex-320">근무시간</div>
                                <div className="w-[40px]"></div>
                              </div>
                              <div className="h-[calc(100%-34px)] w-full overflow-auto">
                                {data.result.data.map((operation) => (
                                  <div className={tw(smallTableRowTextStyle())}>
                                    <div className="flex-180">
                                      {
                                        operationCodeToName[
                                          operation.operationCode
                                        ].split("] ")[
                                          operationCodeToName[
                                            operation.operationCode
                                          ].split("] ").length - 1
                                        ]
                                      }
                                    </div>
                                    <div className="flex-220">
                                      {operation.department}
                                    </div>
                                    <div className="flex-130">
                                      {operation.doctorName}
                                    </div>
                                    <div className="flex-180">
                                      {operation.phoneNumber}
                                    </div>
                                    <div className="flex-320">
                                      <div>{operation.workTime}</div>
                                      <div
                                        className={tw(
                                          "flex items-center justify-center",
                                          "ml-auto",
                                          "text-sm font-semibold",
                                          operation.isAvailable
                                            ? "text-green"
                                            : "text-red"
                                        )}
                                      >
                                        {operation.isAvailable
                                          ? "가능"
                                          : "불가능"}
                                      </div>
                                    </div>
                                    <div className="w-[40px]">
                                      <ChatBubble
                                        className="w-[18px] h-[16px] cursor-pointer"
                                        onClick={() => {
                                          handleOpenChatPopup();
                                        }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="px-[10px] text-base font-regular text-gray-g300">
                              - 해당 사항 없음
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="w-full h-fit bg-white">
                      <div className="flex items-center h-[32px] gap-[20px]">
                        <div className="text-base font-semibold text-gray-g300 leading-[22px]">
                          지역 내 병원 자원현황
                        </div>
                        <div className="flex items-center gap-[4px]">
                          <FollowUpIcon className="w-[14px] h-[14px]" />
                          <div className="text-sm font-medium text-purple-100">
                            유사증상 진단 병원
                          </div>
                        </div>
                        {/* <button
                          className={tw(
                            "ml-auto flex items-center gap-[10px] w-fit h-[32px] px-[16px] bg-white",
                            "border-solid border-[1px] border-gray-g50 rounded-[6px]"
                          )}
                          onClick={() => {
                            window.alert("준비 중입니다.");
                          }}
                        >
                          <FilterIcon className="w-[13px] h-[16px] fill-gray-g300" />
                          <div className="text-sm font-medium text-gray-g300">
                            필터
                          </div>
                        </button> */}
                      </div>
                      <div className="w-full h-fit mt-[10px] overflow-auto">
                        <HospitalResourceGrid />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isICUModalOpen && (
        <Modal
          onClose={() => {
            setIsICUModalOpen(false);
          }}
        >
          <ICUModal
            hospitalCode="A1300002"
            onClose={() => {
              setIsICUModalOpen(false);
            }}
          />
        </Modal>
      )}
    </>
  );
};
